/**
 * 分享到 PC 微信（微信开放平台 PC OpenSDK）。
 *
 * ## 这套能力在什么环境下才有用
 *
 * 只在 **Windows / Mac 的桌面浏览器 + 已登录的微信 4.1.0 及以上客户端**
 * 里有效。`wxopensdk.js` 会往页面里嵌一个 `open.weixin.qq.com` 的 iframe，
 * 由那个 iframe 去跟本机微信通信 —— 手机上根本没有这个通道，所以：
 *
 *   · `isDesktopWechatSharePossible()` 为假时**不要**调接口，直接引导
 *     「复制链接发到微信」，别让用户点了之后看见一个看不懂的错误；
 *   · 微信客户端没开、没登录、被锁屏，都会落到 errcode 6（超时）或
 *     errcode 1（环境不合法），文案要照着这两种情况写。
 *
 * ## 谁负责什么
 *
 *   后端：用 AppSecret 换 access_token，再换一张 5 分钟、只能**用一次**的
 *         ticket（`/api/auth/wechat/pc-ticket`）。AppSecret 绝不能进浏览器。
 *   前端：引脚本 → 等 `wxopensdk.ready` → 带着 ticket 调 `shareLink`。
 *
 * ⚠️ 顺序上的一条注意：ticket 只有 5 分钟且**一次性**，所以脚本加载必须有
 * 超时兜底（`waitReady` 的 8 秒），不能无限等 —— 等到票过期，用户点了没反应，
 * 只能再点第二次。
 *
 * ## 频率限制
 *
 * 微信侧按**用户维度**限流：1 次/秒、5 次/分钟。连点必然吃到 errcode 8，
 * 所以调用方要有 busy 态，别指望这里排队。
 */

import { api } from './api'

/** 后端发的票 + 微信侧约定（脚本地址、卡片图）都在这一份里。 */
export type WechatPcTicket = {
	ticket: string
	appid: string
	ttl: number
	sdk_url: string
	thumb: string
}

export type WechatPcShareArgs = {
	/** 分享出去的链接（一般是曲谱的分享页） */
	url: string
	/** 卡片标题 */
	title: string
	/** 卡片描述 */
	desc?: string
	/** chat = 发给朋友 / timeline = 朋友圈 */
	scene?: 'chat' | 'timeline'
}

type WxOpenSdk = {
	ready?: boolean
	onReady?: () => void
	shareLink: (args: Record<string, unknown>) => Promise<{errcode?: number, errmsg?: string, actionId?: string}>
	error?: (cb: (res: unknown) => void) => void
}

declare global {
	interface Window {
		wxopensdk?: WxOpenSdk
	}
}

/**
 * 分享失败。`code` 是给调用方分流用的（要不要提示"复制链接"、要不要说
 * "去申请权限"），`detail` 是给排查用的原文，别吞掉。
 */
export class WechatPcShareError extends Error {
	readonly code: string
	readonly detail: string

	constructor(message: string, code: string, detail = '') {
		super(message)
		this.name = 'WechatPcShareError'
		this.code = code
		this.detail = detail
	}
}

/**
 * errcode → 人话。
 *
 * 码表来自开放平台文档「errcode 说明」，**每一项都对应一个具体动作**：
 * 说"分享失败"等于什么都没说，用户只能来找你。
 */
function explainErrcode(errcode: number, errmsg: string): WechatPcShareError {
	const detail = `${errcode} ${errmsg}`.trim()
	switch (errcode) {
		case 0:
			return new WechatPcShareError('分享已发起', 'ok', detail)
		case 1:
			return new WechatPcShareError('当前浏览器环境不支持直接分享到微信（需要 Windows / Mac 上安装并登录微信 4.1.0 及以上版本）', 'env_unsupported', detail)
		case 2:
			return new WechatPcShareError('与本机微信通信失败（网络错误），请稍后重试', 'network', detail)
		case 3:
			return new WechatPcShareError('分享票据已失效，请再点一次', 'ticket', detail)
		case 4:
			return new WechatPcShareError('分享参数有误，请联系站长', 'bad_args', detail)
		case 5:
			return new WechatPcShareError('微信侧异常，请稍后重试', 'wechat_error', detail)
		case 6:
			return new WechatPcShareError('没有检测到正在运行的微信。请先在电脑上打开并登录微信，再点一次', 'no_wechat', detail)
		case 7:
			return new WechatPcShareError('分享失败（未知错误），请稍后重试', 'unknown', detail)
		case 8:
			return new WechatPcShareError('分享太频繁了，请等几秒再试', 'too_frequent', detail)
		case -11029:
			return new WechatPcShareError('分享票据无效，请再点一次', 'ticket', detail)
		case -11032:
			return new WechatPcShareError('分享域名未在微信开放平台登记（业务域名），请联系站长', 'domain_unregistered', detail)
		case -11033:
			return new WechatPcShareError('分享需要在 https 页面进行', 'need_https', detail)
		case -11034:
			return new WechatPcShareError('本站还没有开通「发送给朋友 / 分享到朋友圈」权限，暂时不能直接分享', 'no_permission', detail)
		case -11036:
			return new WechatPcShareError('微信客户端版本过低，请升级到 4.1.0 及以上', 'client_too_old', detail)
		default:
			return new WechatPcShareError(`分享失败（${detail}）`, 'failed', detail)
	}
}

/** 移动端 / 非桌面浏览器：这套能力用不上，应引导复制链接。 */
export function isDesktopWechatSharePossible(): boolean {
	if (typeof window === 'undefined') return false
	// 桌面浏览器才可能有本机微信客户端；手机浏览器一律走"复制链接"。
	return !/Android|iPhone|iPad|iPod|Mobile|HarmonyOS/i.test(navigator.userAgent)
}

let sdkPromise: Promise<WxOpenSdk> | null = null

/**
 * 引入 `wxopensdk.js`。同一页面只引一次（脚本自己会挂 iframe）。
 *
 * `url` 由后端下发：微信换地址时改后端就行，不用重新发前端包。
 */
function loadSdk(url: string): Promise<WxOpenSdk> {
	if (window.wxopensdk) return Promise.resolve(window.wxopensdk)
	if (sdkPromise) return sdkPromise

	sdkPromise = new Promise<WxOpenSdk>((resolve, reject) => {
		const el = document.createElement('script')
		el.src = url
		el.async = true
		el.onload = () => {
			if (window.wxopensdk) resolve(window.wxopensdk)
			else reject(new WechatPcShareError('微信分享脚本加载后没有就绪，请刷新页面重试', 'sdk_missing'))
		}
		el.onerror = () => {
			// 失败要清掉缓存，否则一次网络抖动会让这个页面此后永久失败
			sdkPromise = null
			reject(new WechatPcShareError('微信分享脚本加载失败（可能被浏览器插件或网络拦截）', 'sdk_load_failed'))
		}
		document.head.appendChild(el)
	})
	return sdkPromise
}

/** 等 `wxopensdk.ready`。脚本自己会回调 `onReady`，这里再加一道超时兜底。 */
function waitReady(sdk: WxOpenSdk, timeoutMs = 8000): Promise<void> {
	if (sdk.ready) return Promise.resolve()
	return new Promise<void>((resolve, reject) => {
		const timer = setTimeout(() => {
			reject(new WechatPcShareError('微信分享组件初始化超时，请刷新页面重试', 'sdk_timeout'))
		}, timeoutMs)
		sdk.onReady = () => {
			clearTimeout(timer)
			resolve()
		}
	})
}

/**
 * Chrome 142+ 对「访问本地网络」加了权限提示，被拒时分享必然失败。
 *
 * 主动查一次是为了**说清原因**：否则用户看到的是 errcode 1（环境不合法），
 * 完全想不到要去地址栏左边把权限打开。
 */
async function assertLocalNetworkAllowed(): Promise<void> {
	try {
		const status = await (navigator.permissions as any)?.query?.({name: 'local-network-access'})
		if (status?.state === 'denied') {
			throw new WechatPcShareError(
				'浏览器禁止了本站访问本地网络，无法连到本机微信。请点击地址栏左侧的站点信息，把「本地网络访问」改为允许后重试',
				'local_network_denied',
			)
		}
	} catch (err) {
		// 浏览器不认识这个权限名（或 query 抛错）→ 当作"没有这道限制"，继续往下走。
		// ⚠️ 不能把这里抛的错当成失败：绝大多数浏览器根本没有这个权限项。
		if (err instanceof WechatPcShareError) throw err
	}
}

/**
 * 取票 → 载入 SDK → 发起分享。
 *
 * 成功即 resolve；任何一步失败都抛 `WechatPcShareError`，由调用方决定
 * 弹什么（通常是"复制链接"的兜底）。
 */
export async function shareToWechatPc(args: WechatPcShareArgs): Promise<void> {
	if (!isDesktopWechatSharePossible()) {
		throw new WechatPcShareError(
			'手机浏览器不能直接分享到电脑微信，请用「复制链接」发到微信',
			'mobile',
		)
	}
	await assertLocalNetworkAllowed()

	// 一次请求把票和"微信侧约定"（脚本地址、卡片图）全拿到。
	// 票 5 分钟内有效，而脚本加载有 8 秒超时兜底 —— 顺序上不会把票等废；
	// 万一脚本加载失败，这次票作废，下次点击会重新取（票只在调用时消耗）。
	const info = await api.get<WechatPcTicket>('/auth/wechat/pc-ticket')
	const sdk = await loadSdk(info.sdk_url)
	await waitReady(sdk)

	let res: {errcode?: number, errmsg?: string, actionId?: string}
	try {
		res = await sdk.shareLink({
			appid: info.appid,
			txt: args.title,
			desc: args.desc ?? '',
			url: args.url,
			thumburl: info.thumb,
			scene: args.scene ?? 'chat',
			ticket: info.ticket,
		})
	} catch (err: any) {
		// 老版本 SDK 在环境不合法时是 reject 一个字符串/对象，不是 resolve errcode
		throw new WechatPcShareError(
			`与本机微信通信失败：${String(err?.errmsg ?? err?.message ?? err)}`,
			'call_failed',
			String(err),
		)
	}

	const errcode = Number(res?.errcode ?? 0)
	if (errcode !== 0) throw explainErrcode(errcode, String(res?.errmsg ?? ''))
}
